import { Tabs, Typography } from '@scale/scaleui';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';

import { TeamLeadPermissionRole } from '@scale/shared/types/expertManagement';
import { useQuery } from 'react-query';
import { fetchGlobalStats } from 'react-scale/components/ExpertManagement/ExpertManagementPage/api';
import { communityCards } from 'react-scale/components/ExpertManagement/ExpertManagementPage/cards';
import { useFilteredPodTypeOptions } from 'react-scale/components/ExpertManagement/hooks/useFilteredPodTypeOptions';
import { useIsCurrentUserAuthorized } from 'react-scale/components/ExpertManagement/hooks/useIsCurrentUserAuthorized';
import {
  StatContextState,
  apiStatContextSelector,
  dateRangeSelector,
  podTypeFilterSelector,
  projectIdSelector,
  setPodTypeFilterSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import shallow from 'zustand/shallow';
import { CommunityManagementTab } from './CommunityManagement/CommunityManagementTab';
import { ManagerAggregationTable } from './ManagerAggregationTable';
import { StatContextSelector } from './StatContextSelector';
import { StatisticRow } from './StatisticRow';
import { ManagerStats } from './types';

const selectors = (state: StatContextState) => ({
  projectId: projectIdSelector(state),
  dateRange: dateRangeSelector(state),
  podTypeFilter: podTypeFilterSelector(state),
  apiStatContext: apiStatContextSelector(state),
  setPodTypeFilter: setPodTypeFilterSelector(state),
});

interface GlobalPageProps {
  isRemoAdmin?: boolean;
}

const GlobalPage: React.FC<GlobalPageProps> = ({ isRemoAdmin = false }) => {
  const history = useHistory();
  const { podType } = queryString.parse(location.search);
  const [selectedTab, setSelectedTab] = useState<string>('Top-Level Leaders');
  const { projectId, dateRange, podTypeFilter, setPodTypeFilter, apiStatContext } =
    useStatContextStore(selectors, shallow);
  const filteredPodTypes = useFilteredPodTypeOptions();
  const useNewSquadManagementUI = useUnleashFeatureFlag('squad-management-new-ui', {}, false);

  useEffect(() => {
    if (!isRemoAdmin && !!podType && podType !== podTypeFilter) setPodTypeFilter(podType as string);
  }, [isRemoAdmin, podType, podTypeFilter, setPodTypeFilter]);

  const canManageCommunity = useIsCurrentUserAuthorized(
    [TeamLeadPermissionRole.GlobalCommunityManager, TeamLeadPermissionRole.PodTypeCommunityManager],
    podTypeFilter,
  );

  const stats = useQuery(
    ['globalStats', projectId, dateRange, podTypeFilter],
    () => fetchGlobalStats(apiStatContext),
    {
      staleTime: 60 * 60 * 1000,
    },
  );

  const cardRows = useMemo(() => {
    if (!stats.data?.stats) {
      return [];
    }
    const current: ManagerStats = stats.data.stats.current;
    const previous: ManagerStats = stats.data.stats.previous;
    return communityCards(current, previous);
  }, [stats.data]);

  const handleSelectUser = useCallback(
    (user: any) => {
      const userId = user.value;
      history.push(buildRoute(Routes.ExpertManagement, { user: userId }));
    },
    [history],
  );
  const pageTitle = isRemoAdmin
    ? 'Team Lead Dashboard'
    : filteredPodTypes.find(pod => pod.id === podTypeFilter)?.name ?? 'Team Lead Dashboard';

  return (
    <div className="p-8 h-full flex flex-col items-start gap-4">
      <Typography.H3>{pageTitle}</Typography.H3>
      <StatContextSelector />
      <div>
        <StatisticRow
          title="Global Statistics"
          cards={cardRows}
          loadingCards={stats.isLoading ? 5 : 0}
        />
      </div>
      <div className="bg-neutral-0 p-4 border border-neutral-200 rounded-md self-stretch">
        {!useNewSquadManagementUI && canManageCommunity && (
          <Tabs
            value={selectedTab}
            onChange={val => setSelectedTab(val)}
            tabs={[{ id: 'Top-Level Leaders' }, { id: 'Community Management' }]}
          />
        )}
        <div className="py-4">
          {selectedTab === 'Top-Level Leaders' && (
            <div>
              <div className="my-4">
                <SelectScaler
                  placeholder="Select user to view their profile"
                  onChange={handleSelectUser}
                  filterOption={() => true}
                  className="z-50"
                />
              </div>
              <ManagerAggregationTable />
            </div>
          )}
          {selectedTab === 'Community Management' && <CommunityManagementTab />}
        </div>
      </div>
    </div>
  );
};

export default GlobalPage;
